/**
 * Detection Icon Configuration
 *
 * Maps AI model detection class names to Phosphor icons + colors.
 * Each entry: { icon, color (Tailwind text class), label (human-readable) }
 *
 * HOW TO CUSTOMISE:
 *   - Add/edit entries in OBJECT_DETECTION_ICONS for visual object classes (COCO, lowercase).
 *   - Add/edit entries in AUDIO_DETECTION_ICONS for audio event classes (Title_case, matches backend exactly).
 *   - Keys must match backend class_name strings exactly (case-sensitive).
 *   - For new AI models with different class sets, extend the maps with the model's class names.
 */

/*
 * Icons are imported one module deep (`@phosphor-icons/react/dist/csr/<Name>`) rather than
 * from the package root. The root is a barrel over the entire icon set, so Vite's dependency
 * pre-bundler turns these 36 icons into a single 5.9 MB dev chunk that the browser downloads
 * on every cold load. `./dist/csr/*` and `./lib` are both declared in the package's `exports`
 * map, so these are public entry points. See docs/option-c-changes/148-phosphor-deep-imports.md.
 */
import type { Icon as PhosphorIcon } from '@phosphor-icons/react/lib'
// Person & body
import { Person } from '@phosphor-icons/react/dist/csr/Person'
// Vehicles
import { Bicycle } from '@phosphor-icons/react/dist/csr/Bicycle'
import { Car } from '@phosphor-icons/react/dist/csr/Car'
import { Motorcycle } from '@phosphor-icons/react/dist/csr/Motorcycle'
import { Airplane } from '@phosphor-icons/react/dist/csr/Airplane'
import { Bus } from '@phosphor-icons/react/dist/csr/Bus'
import { Train } from '@phosphor-icons/react/dist/csr/Train'
import { Truck } from '@phosphor-icons/react/dist/csr/Truck'
import { Boat } from '@phosphor-icons/react/dist/csr/Boat'
// Animals
import { Bird } from '@phosphor-icons/react/dist/csr/Bird'
import { Cat } from '@phosphor-icons/react/dist/csr/Cat'
import { Dog } from '@phosphor-icons/react/dist/csr/Dog'
import { Horse } from '@phosphor-icons/react/dist/csr/Horse'
// Generic / fallback
import { Scan } from '@phosphor-icons/react/dist/csr/Scan'
import { SpeakerHigh } from '@phosphor-icons/react/dist/csr/SpeakerHigh'
// Kitchen & food
import { Wine } from '@phosphor-icons/react/dist/csr/Wine'
import { Knife } from '@phosphor-icons/react/dist/csr/Knife'
// Appliances & electronics
import { Television } from '@phosphor-icons/react/dist/csr/Television'
import { Laptop } from '@phosphor-icons/react/dist/csr/Laptop'
import { Mouse } from '@phosphor-icons/react/dist/csr/Mouse'
import { Keyboard } from '@phosphor-icons/react/dist/csr/Keyboard'
import { DeviceMobile } from '@phosphor-icons/react/dist/csr/DeviceMobile'
// Furniture
import { Chair } from '@phosphor-icons/react/dist/csr/Chair'
import { Toilet } from '@phosphor-icons/react/dist/csr/Toilet'
// Indoor objects
import { Book } from '@phosphor-icons/react/dist/csr/Book'
import { Clock } from '@phosphor-icons/react/dist/csr/Clock'
import { Scissors } from '@phosphor-icons/react/dist/csr/Scissors'
// Audio event icons
import { Target } from '@phosphor-icons/react/dist/csr/Target'
import { Megaphone } from '@phosphor-icons/react/dist/csr/Megaphone'
import { ShieldWarning } from '@phosphor-icons/react/dist/csr/ShieldWarning'
import { Footprints } from '@phosphor-icons/react/dist/csr/Footprints'
import { Warning } from '@phosphor-icons/react/dist/csr/Warning'
import { Wind } from '@phosphor-icons/react/dist/csr/Wind'
import { Microphone } from '@phosphor-icons/react/dist/csr/Microphone'
import { Smiley } from '@phosphor-icons/react/dist/csr/Smiley'
import { Moon } from '@phosphor-icons/react/dist/csr/Moon'

export interface DetectionIconConfig {
  icon: PhosphorIcon
  color: string    // Tailwind CSS text color class
  label: string    // Human-readable display label
}

// ============================================================================
// Visual Object Detection Classes
// 80 COCO classes — keys are lowercase, matching backend detected_objects[]
// ============================================================================

export const OBJECT_DETECTION_ICONS: Record<string, DetectionIconConfig> = {
  // --- Person ---
  person:           { icon: Person,        color: 'text-blue-500',     label: 'Person' },

  // --- Vehicles ---
  bicycle:          { icon: Bicycle,       color: 'text-green-500',    label: 'Bicycle' },
  car:              { icon: Car,           color: 'text-orange-500',   label: 'Car' },
  motorcycle:       { icon: Motorcycle,    color: 'text-orange-400',   label: 'Motorcycle' },
  airplane:         { icon: Airplane,      color: 'text-sky-500',      label: 'Airplane' },
  bus:              { icon: Bus,           color: 'text-orange-600',   label: 'Bus' },
  train:            { icon: Train,         color: 'text-slate-500',    label: 'Train' },
  truck:            { icon: Truck,         color: 'text-amber-600',    label: 'Truck' },
  boat:             { icon: Boat,          color: 'text-blue-400',     label: 'Boat' },

  // --- Traffic infrastructure ---
  'stop sign':      { icon: Warning,       color: 'text-red-600',      label: 'Stop Sign' },
  'parking meter':  { icon: Clock,         color: 'text-slate-400',    label: 'Parking Meter' },
  bench:            { icon: Chair,         color: 'text-amber-400',    label: 'Bench' },

  // --- Animals ---
  bird:             { icon: Bird,          color: 'text-yellow-400',   label: 'Bird' },
  cat:              { icon: Cat,           color: 'text-amber-400',    label: 'Cat' },
  dog:              { icon: Dog,           color: 'text-amber-500',    label: 'Dog' },
  horse:            { icon: Horse,         color: 'text-amber-700',    label: 'Horse' },
  sheep:            { icon: Scan,          color: 'text-gray-400',     label: 'Sheep' },
  cow:              { icon: Scan,          color: 'text-amber-600',    label: 'Cow' },
  elephant:         { icon: Scan,          color: 'text-gray-500',     label: 'Elephant' },
  bear:             { icon: Scan,          color: 'text-amber-800',    label: 'Bear' },
  zebra:            { icon: Scan,          color: 'text-gray-700',     label: 'Zebra' },
  giraffe:          { icon: Scan,          color: 'text-yellow-600',   label: 'Giraffe' },

  // --- Outdoor / sport ---
  backpack:         { icon: Scan,          color: 'text-slate-500',    label: 'Backpack' },
  umbrella:         { icon: Scan,          color: 'text-blue-300',     label: 'Umbrella' },
  handbag:          { icon: Scan,          color: 'text-pink-400',     label: 'Handbag' },
  tie:              { icon: Scan,          color: 'text-purple-400',   label: 'Tie' },
  suitcase:         { icon: Scan,          color: 'text-slate-500',    label: 'Suitcase' },
  frisbee:          { icon: Scan,          color: 'text-green-400',    label: 'Frisbee' },
  skis:             { icon: Scan,          color: 'text-sky-400',      label: 'Skis' },
  snowboard:        { icon: Scan,          color: 'text-sky-300',      label: 'Snowboard' },
  'sports ball':    { icon: Scan,          color: 'text-orange-400',   label: 'Sports Ball' },
  kite:             { icon: Wind,          color: 'text-sky-400',      label: 'Kite' },
  'baseball bat':   { icon: Scan,          color: 'text-amber-500',    label: 'Baseball Bat' },
  'baseball glove': { icon: Scan,          color: 'text-amber-600',    label: 'Baseball Glove' },
  skateboard:       { icon: Scan,          color: 'text-slate-500',    label: 'Skateboard' },
  surfboard:        { icon: Scan,          color: 'text-blue-400',     label: 'Surfboard' },
  'tennis racket':  { icon: Scan,          color: 'text-yellow-400',   label: 'Tennis Racket' },

  // --- Kitchen & dining ---
  bottle:           { icon: Scan,          color: 'text-green-400',    label: 'Bottle' },
  'wine glass':     { icon: Wine,          color: 'text-purple-400',   label: 'Wine Glass' },
  knife:            { icon: Knife,         color: 'text-slate-500',    label: 'Knife' },
  spoon:            { icon: Scan,          color: 'text-slate-400',    label: 'Spoon' },

  // --- Food ---
  banana:           { icon: Scan,          color: 'text-yellow-400',   label: 'Banana' },
  apple:            { icon: Scan,          color: 'text-red-400',      label: 'Apple' },
  sandwich:         { icon: Scan,          color: 'text-amber-400',    label: 'Sandwich' },
  orange:           { icon: Scan,          color: 'text-orange-400',   label: 'Orange' },
  broccoli:         { icon: Scan,          color: 'text-green-500',    label: 'Broccoli' },
  carrot:           { icon: Scan,          color: 'text-orange-500',   label: 'Carrot' },
  'hot dog':        { icon: Scan,          color: 'text-orange-400',   label: 'Hot Dog' },
  pizza:            { icon: Scan,          color: 'text-orange-500',   label: 'Pizza' },
  donut:            { icon: Scan,          color: 'text-pink-400',     label: 'Donut' },
  cake:             { icon: Scan,          color: 'text-pink-300',     label: 'Cake' },

  // --- Furniture ---
  chair:            { icon: Chair,         color: 'text-amber-500',    label: 'Chair' },
  couch:            { icon: Scan,          color: 'text-amber-400',    label: 'Couch' },
  'potted plant':   { icon: Scan,          color: 'text-green-500',    label: 'Potted Plant' },
  bed:              { icon: Scan,          color: 'text-blue-300',     label: 'Bed' },
  'dining table':   { icon: Scan,          color: 'text-amber-600',    label: 'Dining Table' },
  toilet:           { icon: Toilet,        color: 'text-slate-400',    label: 'Toilet' },

  // --- Electronics ---
  tv:               { icon: Television,    color: 'text-slate-500',    label: 'TV' },
  laptop:           { icon: Laptop,        color: 'text-slate-500',    label: 'Laptop' },
  mouse:            { icon: Mouse,         color: 'text-slate-400',    label: 'Mouse' },
  remote:           { icon: Scan,          color: 'text-slate-400',    label: 'Remote' },
  keyboard:         { icon: Keyboard,      color: 'text-slate-500',    label: 'Keyboard' },
  'cell phone':     { icon: DeviceMobile,  color: 'text-slate-500',    label: 'Cell Phone' },
  microwave:        { icon: Scan,          color: 'text-slate-400',    label: 'Microwave' },
  oven:             { icon: Scan,          color: 'text-slate-500',    label: 'Oven' },
  toaster:          { icon: Scan,          color: 'text-slate-400',    label: 'Toaster' },
  sink:             { icon: Scan,          color: 'text-blue-300',     label: 'Sink' },
  refrigerator:     { icon: Scan,          color: 'text-sky-300',      label: 'Refrigerator' },

  // --- Indoor objects ---
  book:             { icon: Book,          color: 'text-amber-500',    label: 'Book' },
  clock:            { icon: Clock,         color: 'text-slate-500',    label: 'Clock' },
  vase:             { icon: Scan,          color: 'text-purple-300',   label: 'Vase' },
  scissors:         { icon: Scissors,      color: 'text-slate-500',    label: 'Scissors' },
  'teddy bear':     { icon: Smiley,        color: 'text-amber-400',    label: 'Teddy Bear' },
  'hair drier':     { icon: Wind,          color: 'text-pink-300',     label: 'Hair Drier' },
  toothbrush:       { icon: Scan,          color: 'text-blue-300',     label: 'Toothbrush' },
}

// ============================================================================
// Audio Event Detection Classes
// 19 classes — keys are Title_case, matching backend audio_detections[].class_name exactly
// ============================================================================

export const AUDIO_DETECTION_ICONS: Record<string, DetectionIconConfig> = {
  Aerosol_spray:  { icon: Wind,          color: 'text-slate-400',    label: 'Graffiti' },
  Aircraft_noise: { icon: Airplane,      color: 'text-sky-400',      label: 'Aircraft' },
  Car_backfire:   { icon: Car,           color: 'text-orange-500',   label: 'Car Backfire' },
  Cough:          { icon: Microphone,    color: 'text-amber-400',    label: 'Cough' },
  Drone_flying:   { icon: Scan,          color: 'text-sky-500',      label: 'Drone' },
  Footstep:       { icon: Footprints,    color: 'text-slate-400',    label: 'Footstep' },
  Glass_break:    { icon: ShieldWarning, color: 'text-yellow-500',   label: 'Glass Break' },
  Gunshot:        { icon: Target,        color: 'text-red-500',      label: 'Gunshot' },
  Hiccup:         { icon: Microphone,    color: 'text-slate-300',    label: 'Hiccup' },
  Moan:           { icon: Microphone,    color: 'text-slate-400',    label: 'Moan' },
  Rustle:         { icon: Wind,          color: 'text-slate-300',    label: 'Rustle' },
  Scream:         { icon: Megaphone,     color: 'text-red-400',      label: 'Scream' },
  Sneeze:         { icon: Wind,          color: 'text-blue-300',     label: 'Sneeze' },
  Snore:          { icon: Moon,          color: 'text-indigo-300',   label: 'Snore' },
  Throat_clear:   { icon: Microphone,    color: 'text-slate-400',    label: 'Throat Clear' },
  Vomit:          { icon: Warning,       color: 'text-yellow-600',   label: 'Vomit' },
  Yawn:           { icon: Moon,          color: 'text-slate-300',    label: 'Yawn' },
  Yell:           { icon: Megaphone,     color: 'text-orange-500',   label: 'Yell' },
  Others:         { icon: SpeakerHigh,   color: 'text-muted-foreground', label: 'Other Sound' },
}

// ============================================================================
// Fallbacks for any class name not in the maps above
// ============================================================================

export const DEFAULT_OBJECT_ICON: DetectionIconConfig = {
  icon: Scan,
  color: 'text-muted-foreground',
  label: '',
}

export const DEFAULT_AUDIO_ICON: DetectionIconConfig = {
  icon: SpeakerHigh,
  color: 'text-muted-foreground',
  label: '',
}
